﻿
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Share,
  Image,
  Dimensions,
  Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import * as LocalAuthentication from 'expo-local-authentication';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  FontAwesome,
  Feather,
  MaterialIcons,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';

const { width } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';

const LearningProfileScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [activeTab, setActiveTab] = useState('progress');
  const [animatedValue] = useState(new Animated.Value(0));
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    completedCourses: 12,
    activeCourses: 4,
    totalHours: 156,
    currentStreak: 7,
    certificates: 5,
    rank: 'Advanced Learner',
    points: 2450,
  });

  // Dashboard metrics
  const [metrics, setMetrics] = useState({
    weeklyProgress: 85,
    dailyGoal: 60,
    engagement: 78,
    completionRate: 92,
    avgScore: 88,
    timeSpent: 24, // hours this week
  });

  // Color scheme matching dashboard
  const colors = {
    primary: '#4F46E5',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    warning: '#F97316',
    info: '#0EA5E9',
    purple: '#8B5CF6',
    pink: '#EC4899',
    dark: '#1F2937',
    light: '#F9FAFB',
    white: '#FFFFFF',
    gray: '#6B7280',
    lightGray: '#E5E7EB',
  };

  const fadeIn = () => {
    Animated.timing(animatedValue, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  };

  useFocusEffect(
    React.useCallback(() => {
      loadUserData();
      loadProfileImage();
      loadLearningStats();
      fadeIn();
      return () => {
        animatedValue.setValue(0);
      };
    }, [])
  );

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('learning_user_data');
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadProfileImage = async () => {
    try {
      const savedImage = await AsyncStorage.getItem('learning_profile_image');
      if (savedImage) {
        setProfileImage(savedImage);
      }
    } catch (error) {
      console.error('Error loading profile image:', error);
    }
  };

  const loadLearningStats = async () => {
    try {
      const savedStats = await AsyncStorage.getItem('learning_stats');
      if (savedStats) {
        setStats(JSON.parse(savedStats));
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const saveProfileImage = async (imageUri) => {
    try {
      await AsyncStorage.setItem('learning_profile_image', imageUri);
      setProfileImage(imageUri);
      // Optionally upload to server
      // await uploadProfileImage(imageUri);
    } catch (error) {
      console.error('Error saving profile image:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Learning Student';
    return userData.fullName || userData.name || 'Learning Student';
  };

  const getUserEmail = () => {
    if (!userData) return 'student@jobeng.ai';
    return userData.email || 'student@jobeng.ai';
  };

  const getUserLevel = () => {
    return stats.rank || 'Beginner';
  };

  const handleEditProfile = () => {
  navigation.navigate('EditLearningProfileScreen', { userData: userData });
};

  const handleChangeProfilePicture = () => {
    Alert.alert(
      'Update Profile Picture',
      'Choose an option',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Take Photo', onPress: openCamera },
        { text: 'Choose from Gallery', onPress: openGallery },
        ...(profileImage ? [{ text: 'Remove Photo', onPress: removeProfilePicture, style: 'destructive' }] : []),
      ]
    );
  };

  const openCamera = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission needed', 'Camera permission is required to take photos.');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      saveProfileImage(result.assets[0].uri);
    }
  };

  const openGallery = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission needed', 'Gallery permission is required to select photos.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      saveProfileImage(result.assets[0].uri);
    }
  };

  const removeProfilePicture = async () => {
    try {
      await AsyncStorage.removeItem('learning_profile_image');
      setProfileImage(null);
      Alert.alert('Success', 'Profile picture removed');
    } catch (error) {
      console.error('Error removing profile image:', error);
      Alert.alert('Error', 'Unable to remove profile picture');
    }
  };

  const handleShareAchievements = async () => {
    try {
      const shareContent = `🎓 Check out my JOBENG Learning Profile!\n\n👤 ${getUserName()}\n📚 ${stats.completedCourses} courses completed\n⏱️ ${stats.totalHours} hours of learning\n🔥 ${stats.currentStreak} day learning streak\n🏆 Rank: ${stats.rank}\n\nJoin me on JOBENG Learning Platform! 🚀`;
      
      await Share.share({
        message: shareContent,
        title: 'My Learning Achievements',
      });
    } catch (error) {
      console.error('Error sharing profile:', error);
      Alert.alert('Error', 'Unable to share achievements. Please try again.');
    }
  };

  const handleNavigateToSettings = () => {
    navigation.navigate('ProfileSettings');
  };

  const dashboardCards = [
    {
      id: 'progress',
      title: 'Learning Progress',
      icon: 'trending-up',
      value: `${metrics.weeklyProgress}%`,
      color: colors.primary,
      onPress: () => setActiveTab('progress'),
    },
    {
      id: 'streak',
      title: 'Current Streak',
      icon: 'fire',
      value: `${stats.currentStreak} days`,
      color: colors.warning,
      onPress: () => setActiveTab('streak'),
    },
    {
      id: 'points',
      title: 'Learning Points',
      icon: 'star',
      value: stats.points,
      color: colors.accent,
      onPress: () => setActiveTab('points'),
    },
    {
      id: 'rank',
      title: 'Current Rank',
      icon: 'trophy',
      value: stats.rank,
      color: colors.purple,
      onPress: () => setActiveTab('rank'),
    },
  ];

  const quickActions = [
    {
      title: 'My Courses',
      subtitle: 'Continue learning',
      icon: 'book-open',
      color: colors.primary,
      onPress: () => navigation.navigate('MyCourses'),
    },
    {
      title: 'Certificates',
      subtitle: `${stats.certificates} earned`,
      icon: 'certificate',
      color: colors.secondary,
      onPress: () => navigation.navigate('Certificates'),
    },
    {
      title: 'Achievements',
      subtitle: 'View badges',
      icon: 'award',
      color: colors.accent,
      onPress: () => navigation.navigate('Achievements'),
    },
    {
      title: 'Settings',
      subtitle: 'Profile & preferences',
      icon: 'settings',
      color: colors.gray,
      onPress: handleNavigateToSettings,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'progress':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.tabTitle}>Weekly Progress</Text>
            <View style={styles.progressChart}>
              <View style={[styles.progressBar, { width: `${metrics.weeklyProgress}%`, backgroundColor: colors.primary }]} />
            </View>
            <View style={styles.metricsGrid}>
              <View style={styles.metricItem}>
                <Text style={styles.metricValue}>{metrics.dailyGoal}%</Text>
                <Text style={styles.metricLabel}>Daily Goal</Text>
              </View>
              <View style={styles.metricItem}>
                <Text style={styles.metricValue}>{metrics.engagement}%</Text>
                <Text style={styles.metricLabel}>Engagement</Text>
              </View>
              <View style={styles.metricItem}>
                <Text style={styles.metricValue}>{metrics.completionRate}%</Text>
                <Text style={styles.metricLabel}>Completion</Text>
              </View>
              <View style={styles.metricItem}>
                <Text style={styles.metricValue}>{metrics.avgScore}%</Text>
                <Text style={styles.metricLabel}>Avg Score</Text>
              </View>
            </View>
          </View>
        );
      
      case 'streak':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.tabTitle}>Learning Streak</Text>
            <View style={styles.streakContainer}>
              <Ionicons name="flame" size={60} color={colors.warning} />
              <Text style={styles.streakText}>{stats.currentStreak} days</Text>
              <Text style={styles.streakSubtext}>Keep going! Your longest streak was 21 days.</Text>
            </View>
          </View>
        );
      
      case 'points':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.tabTitle}>Learning Points</Text>
            <View style={styles.pointsContainer}>
              <FontAwesome name="star" size={60} color={colors.accent} />
              <Text style={styles.pointsText}>{stats.points}</Text>
              <Text style={styles.pointsSubtext}>Earn 50 more points to level up!</Text>
            </View>
          </View>
        );
      
      case 'rank':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.tabTitle}>Your Rank</Text>
            <View style={styles.rankContainer}>
              <MaterialCommunityIcons name="trophy" size={60} color={colors.purple} />
              <Text style={styles.rankText}>{stats.rank}</Text>
              <Text style={styles.rankSubtext}>Top 15% of all learners</Text>
            </View>
          </View>
        );
      
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={colors.dark} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Learning Profile</Text>
          <TouchableOpacity onPress={handleShareAchievements} style={styles.shareButton}>
            <Feather name="share-2" size={20} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <Animated.View style={[styles.animatedContainer, { opacity: animatedValue }]}>
          {/* Profile Header */}
          <View style={styles.profileHeader}>
            <View style={styles.profileImageContainer}>
              <TouchableOpacity onPress={handleChangeProfilePicture} style={styles.imageTouchable}>
                {profileImage ? (
                  <Image source={{ uri: profileImage || undefined }} style={styles.profileImage} />
                ) : (
                  <View style={styles.defaultProfileImage}>
                    <Ionicons name="person" size={50} color={colors.gray} />
                  </View>
                )}
                <View style={styles.cameraIconContainer}>
                  <Feather name="camera" size={14} color={colors.white} />
                </View>
              </TouchableOpacity>
            </View>
            
            <View style={styles.profileInfo}>
              <Text style={styles.userName}>{getUserName()}</Text>
              <Text style={styles.userEmail}>{getUserEmail()}</Text>
              <View style={styles.levelBadge}>
                <Text style={styles.levelText}>{getUserLevel()}</Text>
              </View>
            </View>
            
            <TouchableOpacity onPress={handleEditProfile} style={styles.editButton}>
            <Feather name="edit-2" size={18} color={colors.primary} />
           </TouchableOpacity>
          </View>

          {/* Dashboard Cards */}
          <View style={styles.dashboardGrid}>
            {dashboardCards.map((card, index) => (
              <TouchableOpacity
                key={card.id}
                style={[
                  styles.dashboardCard,
                  activeTab === card.id && styles.activeCard,
                  { borderLeftColor: card.color }
                ]}
                onPress={card.onPress}
              >
                <View style={[styles.cardIconContainer, { backgroundColor: `${card.color}20` }]}>
                  <Feather name={card.icon} size={20} color={card.color} />
                </View>
                <Text style={styles.cardTitle}>{card.title}</Text>
                <Text style={[styles.cardValue, { color: card.color }]}>{card.value}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Active Tab Content */}
          <View style={styles.tabContainer}>
            {renderTabContent()}
          </View>

          {/* Quick Actions */}
          <View style={styles.quickActionsContainer}>
            <Text style={styles.sectionTitle}>Quick Actions</Text>
            <View style={styles.quickActionsGrid}>
              {quickActions.map((action, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.quickActionCard}
                  onPress={action.onPress}
                >
                  <View style={[styles.actionIconContainer, { backgroundColor: `${action.color}20` }]}>
                    <Feather name={action.icon} size={22} color={action.color} />
                  </View>
                  <Text style={styles.actionTitle}>{action.title}</Text>
                  <Text style={styles.actionSubtitle}>{action.subtitle}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Learning Stats */}
          <View style={styles.statsContainer}>
            <Text style={styles.sectionTitle}>Learning Statistics</Text>
            <View style={styles.statsGrid}>
              <View style={styles.statCard}>
                <MaterialCommunityIcons name="book-check" size={24} color={colors.secondary} />
                <Text style={styles.statNumber}>{stats.completedCourses}</Text>
                <Text style={styles.statLabel}>Completed Courses</Text>
              </View>
              <View style={styles.statCard}>
                <MaterialCommunityIcons name="clock" size={24} color={colors.info} />
                <Text style={styles.statNumber}>{stats.totalHours}h</Text>
                <Text style={styles.statLabel}>Total Hours</Text>
              </View>
              <View style={styles.statCard}>
                <MaterialCommunityIcons name="progress-clock" size={24} color={colors.warning} />
                <Text style={styles.statNumber}>{stats.activeCourses}</Text>
                <Text style={styles.statLabel}>Active Courses</Text>
              </View>
              <View style={styles.statCard}>
                <MaterialCommunityIcons name="certificate" size={24} color={colors.purple} />
                <Text style={styles.statNumber}>{stats.certificates}</Text>
                <Text style={styles.statLabel}>Certificates</Text>
              </View>
            </View>
          </View>

          {/* Recent Activity */}
          <View style={styles.activityContainer}>
            <View style={styles.activityHeader}>
              <Text style={styles.sectionTitle}>Recent Activity</Text>
              <TouchableOpacity>
                <Text style={styles.viewAllText}>View All</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.activityList}>
              <View style={styles.activityItem}>
                <View style={[styles.activityIcon, { backgroundColor: `${colors.secondary}20` }]}>
                  <Feather name="check-circle" size={16} color={colors.secondary} />
                </View>
                <View style={styles.activityContent}>
                  <Text style={styles.activityTitle}>Completed "Advanced React"</Text>
                  <Text style={styles.activityTime}>2 hours ago</Text>
                </View>
                <Text style={styles.activityScore}>95%</Text>
              </View>
              <View style={styles.activityItem}>
                <View style={[styles.activityIcon, { backgroundColor: `${colors.primary}20` }]}>
                  <Feather name="play-circle" size={16} color={colors.primary} />
                </View>
                <View style={styles.activityContent}>
                  <Text style={styles.activityTitle}>Started "Node.js Backend"</Text>
                  <Text style={styles.activityTime}>Yesterday</Text>
                </View>
                <Text style={styles.activityScore}>In Progress</Text>
              </View>
              <View style={styles.activityItem}>
                <View style={[styles.activityIcon, { backgroundColor: `${colors.accent}20` }]}>
                  <Feather name="award" size={16} color={colors.accent} />
                </View>
                <View style={styles.activityContent}>
                  <Text style={styles.activityTitle}>Earned "Fast Learner" badge</Text>
                  <Text style={styles.activityTime}>2 days ago</Text>
                </View>
              </View>
            </View>
          </View>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  shareButton: {
    padding: 8,
  },
  scrollContent: {
    paddingBottom: 30,
  },
  animatedContainer: {
    flex: 1,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    margin: 20,
    padding: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 3,
  },
  profileImageContainer: {
    position: 'relative',
  },
  imageTouchable: {
    position: 'relative',
  },
  profileImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  defaultProfileImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraIconContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: '#4F46E5',
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  profileInfo: {
    flex: 1,
    marginLeft: 16,
  },
  userName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 8,
  },
  levelBadge: {
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  levelText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4F46E5',
  },
  editButton: {
    padding: 8,
    backgroundColor: '#EEF2FF',
    borderRadius: 8,
  },
  dashboardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: 20,
    marginBottom: 20,
  },
  dashboardCard: {
    width: '48%',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    marginHorizontal: '1%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderLeftWidth: 4,
  },
  activeCard: {
    backgroundColor: '#F8FAFC',
    shadowColor: '#4F46E5',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6B7280',
    marginBottom: 4,
  },
  cardValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#4F46E5',
  },
  tabContainer: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  tabContent: {
    alignItems: 'center',
  },
  tabTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
    alignSelf: 'flex-start',
  },
  progressChart: {
    width: '100%',
    height: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
    marginBottom: 20,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
  },
  metricItem: {
    width: '48%',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginBottom: 8,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  streakContainer: {
    alignItems: 'center',
    padding: 20,
  },
  streakText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#F97316',
    marginVertical: 12,
  },
  streakSubtext: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  pointsContainer: {
    alignItems: 'center',
    padding: 20,
  },
  pointsText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#F59E0B',
    marginVertical: 12,
  },
  pointsSubtext: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  rankContainer: {
    alignItems: 'center',
    padding: 20,
  },
  rankText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#8B5CF6',
    marginVertical: 12,
    textAlign: 'center',
  },
  rankSubtext: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  quickActionsContainer: {
    marginHorizontal: 20,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  quickActionCard: {
    width: '48%',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  actionIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  actionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
    textAlign: 'center',
  },
  actionSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  statsContainer: {
    marginHorizontal: 20,
    marginBottom: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statCard: {
    width: '48%',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
    marginVertical: 8,
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  activityContainer: {
    marginHorizontal: 20,
    marginBottom: 30,
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  viewAllText: {
    fontSize: 14,
    color: '#4F46E5',
    fontWeight: '500',
  },
  activityList: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 2,
  },
  activityTime: {
    fontSize: 12,
    color: '#6B7280',
  },
  activityScore: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
  },
});

export default LearningProfileScreen;